"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { Archive, ArrowLeft, ArrowUpRight, ClipboardList, Copy, CornerUpLeft, FileText, Hash, Info, Lock, Maximize2, MessagesSquare, Minimize2, Pin, Smile, Square, Users, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconAttach, IconChevronDown, IconClose, IconEdit, IconLink, IconPlus, IconSearch, IconSend, IconSparkles, IconSpinner, IconTrash } from "@/components/ui/icons";
import { api, API_BASE } from "@/lib/api";
import { EmojiPicker } from "@/components/ui/emoji-picker";
import { сЭмодзи, толькоЭмодзи, КрупныеЭмодзи } from "@/lib/emoji-render";
import { getActiveView } from "@/lib/active-view";
import type { AiConversation, AiMessage, ChatMessage, ChatUser, TeamAttachment, TeamTask, TmChannel, TmChannelInfo } from "@/lib/types";
import { prioColor } from "@/lib/task-priority";
import { RequestsTab } from "@/components/dev-request-modal";
import { TaskCreatePanel } from "@/components/it-tasks/task-create-panel";
import { TaskDetailPanel } from "@/components/it-tasks/it-tasks-page";
import { AttachmentList } from "@/components/team-attachments";
import { AuthImage } from "@/components/ui/auth-image";
import { VoiceButton } from "@/components/ui/voice-button";
import { AiSpecActions } from "@/components/ai-spec-actions";
import { Z } from "@/lib/z";

// Аватар отдаётся публично (/api/users/avatars/…) → обычный <img>, абсолютный origin.
const AVATAR_ORIGIN = API_BASE.replace(/\/api\/?$/, "");
function avatarSrc(u?: string | null): string | undefined {
  if (!u) return undefined;
  if (/^https?:\/\//.test(u)) return u;
  return AVATAR_ORIGIN + (u.startsWith("/") ? u : `/${u}`);
}
// Кружок-аватар: фото при наличии, иначе буква-инициал на цвете отдела.
function UserAvatar({ name, color, url, size = 36 }: { name: string; color: string; url?: string | null; size?: number }) {
  const src = avatarSrc(url);
  const s: React.CSSProperties = { width: size, height: size };
  if (src) {
    return <img src={src} alt={name} style={{ ...s, boxShadow: `0 0 0 1.5px color-mix(in srgb, ${color} 55%, transparent)` }}
      className="shrink-0 rounded-full object-cover" />;
  }
  return (
    <span style={{ ...s, background: color, fontSize: Math.round(size * 0.4) }}
      className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white">
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

type FabCorner = "tl" | "tr" | "bl" | "br";
type Tab = "chat" | "tasks" | "requests" | "ai" | "tg";

// Заголовочный шрифт как в дашборде IT (Benzin) — для названий задач.

const inputCls = "field w-full";

// Единое «пустое состояние»: иконка в кружке + заголовок + подсказка действия.
// Вместо голого серого <p> — по дизайн-системе (делать красиво сразу).
function EmptyState({ icon, title, hint, accent }: { icon: React.ReactNode; title: string; hint?: string; accent?: string }) {
  const c = accent || "var(--textMuted)";
  return (
    <div className="flex flex-col items-center gap-2.5 px-5 py-10 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full"
        style={{ background: `color-mix(in srgb, ${c} 12%, var(--surfaceElevated))`, color: c }}>
        {icon}
      </span>
      <p className="text-[12px] font-semibold text-[var(--text)]">{title}</p>
      {hint && <p className="max-w-[240px] text-[11px] leading-relaxed text-[var(--textMuted)]">{hint}</p>}
    </div>
  );
}

/** Панель командной работы из FAB: Чат 1:1 / Задачи между сотрудниками / Доработки. */
export function TeamPanel({
  onClose, corner = "br", isAdmin = false, currentUserId, initialTab = "chat", currentLocation = "", initialAction = null, initialChatUserId = null, initialAiPrompt = null,
}: {
  onClose: () => void;
  corner?: FabCorner;
  isAdmin?: boolean;
  currentUserId: string;
  initialTab?: Tab;
  currentLocation?: string;
  initialAction?: "new-chat" | "new-task" | null;
  initialChatUserId?: string | null;
  /** Готовый вопрос: панель открыли из «Аналитика» кнопкой «спросить». */
  initialAiPrompt?: string | null;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  // Беседа с ассистентом, выбранная в списке чатов (срез «AI»): передаём во
  // вкладку, она открывает её один раз и сбрасывает.
  const [беседаAI, setБеседаAI] = useState<number | "последняя" | null>(null);
  const [query, setQuery] = useState("");
  // subview = открыт под-экран (диалог чата / беседа AI / деталь задачи) — там
  // глобальный поиск бесполезен, прячем его. Дочерние вкладки сами репортят.
  const [subview, setSubview] = useState(false);
  // «шире»: панель раздвигается для чтения длинных ответов AI / детали задачи.
  const [wide, setWide] = useState(false);
  // «✨ Кратко» из чата → промпт-резюме, который AI-вкладка отправит новой беседой.
  const [aiSeed, setAiSeed] = useState<string | null>(initialAiPrompt);
  const summarizeToAi = (prompt: string) => { setAiSeed(prompt); setTab("ai"); };
  // Запоминаем активную вкладку — FAB открывается на ней в следующий раз.
  useEffect(() => {
    try { window.localStorage.setItem("yuc-fab-tab", tab); } catch { /* ignore */ }
    setSubview(false);  // при смене вкладки под-экран точно закрыт
  }, [tab]);
  useEffect(() => {
    try { setWide(window.localStorage.getItem("yuc-fab-wide") === "1"); } catch { /* ignore */ }
  }, []);
  const toggleWide = () => setWide((v) => {
    const nv = !v;
    try { window.localStorage.setItem("yuc-fab-wide", nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });

  // ⚠️ ПАНЕЛЬ ТЯНЕТСЯ ОТ ШАПКИ СТРАНИЦЫ ДО НИЗА ОКНА (Максим, 04.09.2026).
  // Раньше высота была жёсткой (780 px в развёрнутом виде), а снизу
  // резервировалось место под кружок — его больше нет, и внизу оставалась
  // пустая полоса. ВЕРХ — высота шапки портала с отступом, НИЗ — тот же
  // отступ; между ними панель занимает всё, что даёт окно.
  const M = 20, ВЕРХ = 76;
  // 460, а не 420 (Максим, 04.09.2026): в ряд срезов добавилась кнопка «AI»,
  // и четвёртая плашка упиралась в правый край — на крупном шрифте темы её
  // подрезало. Плюс сам ряд теперь переносится, а не обрезается.
  const PANEL_W = wide ? 720 : 460;
  const style: React.CSSProperties = { position: "fixed", zIndex: Z.FLOAT_PANEL };
  style.top = ВЕРХ;   // угол больше не решает: панель всегда от шапки вниз
  if (corner.includes("r")) style.right = M; else style.left = M;
  // Поиск нужен только в списочных экранах чат/задачи/AI (у «Доработок» свои фильтры).
  // ⚠️ У ЧАТА ПОИСК СВОЙ, В ЛЕВОЙ КОЛОНКЕ. Максим, 03.09.2026: «посмотри, где у
  // них находится поиск, и где у нас» — в образце он под названием списка и
  // шириной с колонку, а у нас лежал полосой над всей панелью и на широком
  // экране растягивался на пол-экрана, хотя ищет только по списку слева.
  const showSearch = tab !== "requests" && tab !== "chat" && !subview;

  if (typeof document === "undefined") return null;

  // Глянцевые неоновые вкладки сверху панели (стиль 3D-навигации): активная
  // приподнята и светится своим неоновым цветом, неактивные приглушены.
  // яркие неоновые цвета (синий / фиолетовый / зелёный — как на референсе)
  // ⚠️ ВКЛАДОК ДВЕ, А БЫЛО ПЯТЬ (Максим, 04.09.2026). Убраны:
  //   «Задачи» — доска задач живёт в кабинете, здесь она дублировала её;
  //   «AI» — беседы теперь в общем списке чатов (кнопка «AI» в ряду срезов
  //          и закреплённая строка «Ассистент»);
  //   «Чаты» — архив переписок Telegram; их место в каналах.
  // Сами экраны задач и ассистента НИКУДА НЕ ДЕЛИСЬ: на них по-прежнему ведут
  // веер кружка («Новая задача», «Спросить AI»), строка «Ассистент» и срез
  // «AI» — просто у них больше нет собственного язычка внизу панели.
  const TABS: { id: Tab; label: string; icon: React.ReactNode; color: string }[] = [
    { id: "chat", label: "Чат", icon: <MessagesSquare size={14} />, color: "var(--primary)" },
    { id: "requests", label: "Доработки", icon: <Wrench size={14} />, color: "var(--primary)" },
  ];
  const active = TABS.find((t) => t.id === tab) ?? TABS[0];  // у скрытых экранов язычка нет — подсвечиваем «Чат»
  const flipped = corner.includes("b");  // кружок снизу → вкладки снизу (панель раскрывается вверх)
  // форма вкладки: параллелограм со скосом ВНИЗ-ВЛЕВО (верх шире), крупные скругления.
  // Переворот делаем ГЕОМЕТРИЕЙ пути (Y), а НЕ `transform: scaleY(-1)` — последний в
  // prod-сборке Chromium глушил заливку SVG-пути (вкладка теряла тело). Без трансформа
  // заливка рисуется одинаково на dev и prod.
  // Ширина вкладки подстраивается под их число (3 → 124 как было; 4 → сжимаем,
  // чтобы язычки умещались в ширину панели и не уезжали за край).
  const OVERLAP = 16;
  const TW = Math.min(wide ? 200 : 124, Math.floor(((PANEL_W - 24) + (TABS.length - 1) * OVERLAP) / TABS.length));
  const TH = 36, SL = 18;
  const Y = (y: number) => (flipped ? TH - y : y);
  const TAB_PATH =
    `M 28 ${Y(2)} L ${TW - 10} ${Y(2)} Q ${TW - 2} ${Y(2)} ${TW - 5} ${Y(10)} ` +                       // верх + скругл. право
    `L ${TW - SL + 5} ${Y(TH - 10)} Q ${TW - SL - 2} ${Y(TH - 2)} ${TW - SL - 10} ${Y(TH - 2)} ` +      // скос + низ-право
    `L 10 ${Y(TH - 2)} Q 2 ${Y(TH - 2)} 5 ${Y(TH - 10)} ` +                                              // низ + скругл. лево
    `L ${SL - 5} ${Y(10)} Q ${SL + 2} ${Y(2)} 28 ${Y(2)} Z`;                                            // лев. скос + скругл. верх-лево

  return createPortal(
    <>
      {/* Прозрачный перехватчик: клик мимо панели закрывает её. Слой — ПОД
          модалками (Z.FLOAT_PANEL - 1), иначе он лежит поверх формы, открытой
          из панели, и съедает все клики по ней. */}
      <div className="fixed inset-0" style={{ zIndex: Z.FLOAT_PANEL - 1 }} onClick={onClose} />
      <div style={style} className="relative" onClick={(e) => e.stopPropagation()}>
        {/* ⚠️ ВЫСОТА ФИКСИРОВАННАЯ, А НЕ «НЕ БОЛЬШЕ ЧЕМ» (Максим, 03.09.2026:
            «закрепить высоту, чтобы при переключениях не прыгало»). Было
            `maxHeight`: панель подстраивалась под содержимое, и при переходе
            между вкладками — чат, задачи, доработки — она то вырастала, то
            сжималась, а вместе с ней ездили и вкладки под ней. Теперь высота
            задана, а содержимое прокручивается внутри.
            `min(…dvh, …px)` — чтобы на большом экране панель не растягивалась
            во всю высоту, а на маленьком не вылезала за экран. */}
        <div style={{ width: `min(94vw, ${PANEL_W}px)`, height: `calc(100dvh - ${ВЕРХ + M}px)` }}
          className={`relative flex ${flipped ? "flex-col-reverse" : "flex-col"}`}>
          {/* ⚠️ ЯЗЫЧКОВ ВНИЗУ БОЛЬШЕ НЕТ (Максим, 04.09.2026): «чтобы
              открывалось просто окно чата». Панель — это чат; на задачи,
              ассистента и доработки ведут веер кружка, строка «Ассистент»
              и раздел IT, а не собственный ряд вкладок. Сам механизм вкладок
              оставлен: по нему панель открывается на нужном экране. */}
          {/* поиск + контент — единственный «непрозрачный» бокс, обведённый неоном */}
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface)]">
            {/* неоновый контур: замкнут со всех сторон, верхние углы чуть скруглены, низ — полный радиус. */}
            <div aria-hidden className="pointer-events-none absolute inset-0"
              style={{
                borderRadius: "var(--radius-lg)",  // все углы одинаково скруглены (как низ)
                padding: "2px",   // толщина контура = штрих иконок во вкладках (lucide stroke 2px)
                background: `linear-gradient(120deg, color-mix(in srgb, ${active.color} 38%, #ffffff) 0%, ${active.color} 55%, color-mix(in srgb, ${active.color} 78%, #000000) 100%)`,
                WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                filter: `drop-shadow(0 0 4px color-mix(in srgb, ${active.color} 70%, transparent))`,
                transition: "filter .2s ease",
                zIndex: 40,
              }} />
            {/* поисковая строка (только в списочных экранах) + кнопка «шире» */}
            {showSearch && (
              <div className="flex items-center gap-1.5 border-b border-[var(--border)] bg-[var(--surfaceElevated)] px-2.5 pb-2 pt-1.5">
                <div className="flex flex-1 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5">
                  <IconSearch size={14} className="shrink-0 text-[var(--textMuted)]" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Поиск…"
                    className="field bg-transparent" />
                  {query && (
                    <button type="button" onClick={() => setQuery("")} aria-label="Очистить"
                      className="shrink-0 text-[var(--textMuted)] hover:text-[var(--text)]"><IconClose size={14} /></button>
                  )}
                </div>
                <button type="button" onClick={toggleWide} data-tooltip={wide ? "Свернуть панель" : "Развернуть панель"}
                  aria-label={wide ? "Свернуть панель" : "Развернуть панель"}
                  className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-[var(--textMuted)] transition hover:text-[var(--text)] sm:inline-flex"
                  style={{ boxShadow: "none" }}>
                  {wide ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
              </div>
            )}
            <div key={tab} className="fab-tab-anim min-h-0 flex-1 overflow-y-auto">
              {tab === "chat" && <ChatTab currentUserId={currentUserId} query={query} onQueryChange={setQuery} onSubview={setSubview} initialChatUserId={initialChatUserId} onAiSummarize={summarizeToAi} wide={wide} onToggleWide={toggleWide} onOpenAi={(convId) => { setБеседаAI(convId ?? null); setTab("ai"); }} />}
              {tab === "tasks" && <TasksTab isAdmin={isAdmin} query={query} onSubview={setSubview} autoNew={initialAction === "new-task"} />}
              {tab === "requests" && <RequestsTab isAdmin={isAdmin} />}
              {tab === "ai" && <AiTab openConvId={беседаAI} onConvOpened={() => setБеседаAI(null)} query={query} onSubview={setSubview} wide={wide} onToggleWide={toggleWide} currentLocation={currentLocation} onClose={onClose} autoNew={initialAction === "new-chat"} seedPrompt={aiSeed} onSeedConsumed={() => setAiSeed(null)} />}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ── Чат 1:1 ──────────────────────────────────────────────────────────────────
function ChatTab({ currentUserId, query = "", onQueryChange, onSubview, initialChatUserId, onAiSummarize, wide = false, onToggleWide, onOpenAi }: { currentUserId: string; query?: string; onQueryChange?: (v: string) => void; onToggleWide?: () => void; /** Открыть ассистента: номер — эта беседа, «последняя» — последняя из списка, без всего — список. */ onOpenAi?: (convId?: number | "последняя") => void; onSubview?: (v: boolean) => void; initialChatUserId?: string | null; onAiSummarize?: (prompt: string) => void; /** Панель развёрнута — показываем список и переписку рядом, как в образце. */ wide?: boolean }) {
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [active, setActive] = useState<ChatUser | null>(null);
  // ⚠️ КАНАЛЫ ДОБАВЛЕНЫ РЯДОМ С ЛИЧКОЙ, а не вместо неё (Максим, 03.09.2026,
  // образец внутреннего чата). Открытый диалог — это ЛИБО человек (`active`),
  // ЛИБО канал (`channel`); одновременно не бывает, поэтому вторая переменная,
  // а не переписанная первая: личная переписка работает у людей каждый день.
  const [channels, setChannels] = useState<TmChannel[]>([]);
  const [channel, setChannel] = useState<TmChannel | null>(null);
  const [chInfo, setChInfo] = useState<TmChannelInfo | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [creatingCh, setCreatingCh] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  //: Показываем архив каналов. Их будет примерно по числу отделов, и отжившие
  //: должны уходить с глаз, а не растягивать список (Максим, 03.09.2026).
  const [архив, setАрхив] = useState(false);
  const [архивные, setАрхивные] = useState<TmChannel[]>([]);
  //: Вкладки списка, как в образце: «Все · Каналы · Личные».
  // Срезы списка. «ai» добавлен 04.09.2026: беседы с ассистентом — такие же
  // разговоры, и им место в общем списке рядом с каналами и людьми, а не только
  // за вкладкой внизу панели (Максим).
  const [срез, setСрез] = useState<"all" | "channels" | "dm" | "ai">("all");
  const [беседы, setБеседы] = useState<AiConversation[]>([]);
  // ⚠️ ПЕРЕПИСКА С АССИСТЕНТОМ ЖИВЁТ ЗДЕСЬ ЖЕ, а не в отдельной вкладке
  // (Максим, 04.09.2026): при переходе во вкладку слева вместо списка людей
  // появлялась история бесед, и вернуться к разговору с человеком было
  // некуда. Теперь слева всегда список чатов, справа — что открыли.
  const [аиБеседа, setАиБеседа] = useState<number | string | null>(null);
  // Список бесед тянем только когда срез выбран: на обычном чате лишний запрос
  // ни к чему, а при возврате на срез он обновится сам.
  useEffect(() => {
    if (срез !== "ai") return;
    void (async () => { try { setБеседы(await api.aiConversations()); } catch { /* ignore */ } })();
  }, [срез]);
  const openedInitial = useRef(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkVal, setLinkVal] = useState("");
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [menu, setMenu] = useState<{ m: ChatMessage; x: number; y: number } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function loadUsers() {
    try { setUsers(await api.chatUsers()); } catch { /* ignore */ }
    try { setChannels(await api.tmChannels()); } catch { /* ignore */ }
  }
  async function loadArchived() {
    try { setАрхивные(await api.tmChannels(true)); } catch { /* тихо */ }
  }
  async function loadChannel(id: number) {
    try {
      setMessages(await api.tmChannelMessages(id));
      setChannels((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
    } catch { /* ignore */ }
  }
  async function loadThread(uid: string) {
    try {
      const m = await api.chatThread(uid);
      setMessages(m);
      setUsers((prev) => prev.map((u) => (u.id === uid ? { ...u, unread: 0 } : u)));
    } catch { /* ignore */ }
  }

  useEffect(() => { void loadUsers(); }, []);
  // Из палитры «Написать <коллега>» — открыть его диалог, когда ростер загрузился (один раз).
  useEffect(() => {
    if (openedInitial.current || !initialChatUserId || users.length === 0) return;
    const u = users.find((x) => x.id === initialChatUserId);
    if (u) { openedInitial.current = true; setActive(u); void loadThread(u.id); }
  }, [initialChatUserId, users]);
  // репортим родителю: открыт диалог → под-экран (прячем глобальный поиск)
  useEffect(() => { onSubview?.(!!active); return () => onSubview?.(false); }, [active, onSubview]);
  // черновик сообщения: восстанавливаем недописанное при открытии диалога, сохраняем при вводе
  const draftUserId = useRef<string | null>(null);
  useEffect(() => {
    const id = active?.id ?? null;
    if (id === draftUserId.current) return;  // тот же собеседник — не трогаем ввод
    draftUserId.current = id;
    if (id) { try { setText(localStorage.getItem(`yuc.chat.draft.${id}`) || ""); } catch { setText(""); } }
  }, [active?.id]);
  useEffect(() => {
    const id = active?.id;
    if (!id) return;
    try { if (text.trim()) localStorage.setItem(`yuc.chat.draft.${id}`, text); else localStorage.removeItem(`yuc.chat.draft.${id}`); } catch { /* ignore */ }
  }, [text, active?.id]);
  // поллинг: открыт диалог → тянем тред каждые 5с; иначе обновляем ростер каждые 12с
  useEffect(() => {
    const id = setInterval(() => {
      if (active) void loadThread(active.id);
      else if (channel) void loadChannel(channel.id);
      else void loadUsers();
    }, (active || channel) ? 5000 : 12000);
    return () => clearInterval(id);
  }, [active, channel]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages]);

  async function send() {
    const t = text.trim();
    const link = linkVal.trim();
    if (editing) {                       // режим редактирования своего сообщения
      if (!t) return;
      const ed = editing;
      setText(""); setEditing(null);
      try {
        await api.editMessage(ed.id, t);
        if (channel) await loadChannel(channel.id); else await loadThread(active!.id);
      } catch { /* ignore */ }
      return;
    }
    if ((!t && pendingFiles.length === 0 && !link) || !(active || channel)) return;
    const files = pendingFiles;
    const rid = replyTo?.id ?? null;
    setText(""); setPendingFiles([]); setLinkVal(""); setLinkOpen(false); setReplyTo(null);
    try {
      const msg = channel
        ? await api.tmChannelSend(channel.id, { text: t, reply_to_id: rid })
        : await api.chatSend(active!.id, t, rid);
      for (const f of files) await api.uploadTeamFile("message", msg.id, f);
      if (link) await api.addTeamLink("message", msg.id, link);
      if (channel) await loadChannel(channel.id); else await loadThread(active!.id);
    } catch { /* ignore */ }
  }

  async function delMsg(m: ChatMessage) {
    setMenu(null);
    try { await api.deleteMessage(m.id); await loadThread(active!.id); } catch { /* ignore */ }
  }
  function startEdit(m: ChatMessage) {
    setMenu(null); setReplyTo(null); setEditing(m); setText(m.text);
  }
  function startReply(m: ChatMessage) {
    setMenu(null); setEditing(null); setReplyTo(m);
  }
  async function reactTo(m: ChatMessage, emoji: string) {
    setMenu(null);
    try { await api.reactMessage(m.id, emoji); await loadThread(active!.id); } catch { /* ignore */ }
  }

  // ⚠️ ОДНА ЛЕНТА И ОДНА НАБОРНАЯ НА ЛИЧКУ И КАНАЛ. Сначала у канала была своя
  // упрощённая копия: без вложений, реакций, ответов и правки. Две ленты
  // разошлись бы при первой же доработке — а расхождения такого рода мы весь
  // день и вычищаем (03.09.2026).
  // ⚠️ ЛЕНТА ПО ОБРАЗЦУ ТЕЛЕГРАМА (Максим, 03.09.2026: «этот интерфейс будет
  // всем знакомым»). Что это значит по частям:
  //   • разделитель дня — «Сегодня», «Вчера», «15 июня» плашкой посередине;
  //   • склейка подряд идущих сообщений одного человека: имя и фото рисуем
  //     только у первого, дальше — сплошной блок с малым отступом;
  //   • хвостик у последнего пузыря группы, время и галочки ВНУТРИ пузыря;
  //   • реакции плашками под текстом, там же где их ставят.
  // Механика (ответы, правка, вложения, реакции) не менялась — только вид.
  const лента = (пустоЗаголовок: string, пустоПодсказка: string, сИменами: boolean) => (
    <div className="flex-1 overflow-y-auto px-3 py-2">
      {messages.length === 0 && <EmptyState icon={<MessagesSquare size={20} />} title={пустоЗаголовок} hint={пустоПодсказка} accent="var(--primary)" />}
      {messages.map((m, idx) => {
        const mine = m.sender_id === currentUserId;
        const rep = m.reply_to_id ? messages.find((x) => x.id === m.reply_to_id) : null;
        const пред = idx > 0 ? messages[idx - 1] : null;
        const след = idx + 1 < messages.length ? messages[idx + 1] : null;
        const день = (x: ChatMessage) => new Date(x.created_at + "Z").toDateString();
        const новыйДень = !пред || день(пред) !== день(m);
        // Пять минут — привычный порог склейки: дольше пауза, значит это уже
        // другая реплика, а не продолжение мысли.
        const близко = (a: ChatMessage, b: ChatMessage) =>
          Math.abs(new Date(b.created_at + "Z").getTime() - new Date(a.created_at + "Z").getTime()) < 5 * 60 * 1000;
        const первыйВГруппе = новыйДень || !пред || пред.sender_id !== m.sender_id || !близко(пред, m);
        const последнийВГруппе = !след || след.sender_id !== m.sender_id || день(след) !== день(m) || !близко(m, след);
        return (
          <div key={m.id}>
            {новыйДень && (
              <div className="my-3 flex justify-center">
                <span className="rounded-full px-2.5 py-0.5 text-[11px] text-[var(--textMuted)]"
                      style={{ background: "color-mix(in srgb, var(--textMuted) 14%, transparent)" }}>
                  {датаЛенты(m.created_at)}
                </span>
              </div>
            )}
            <div className={`flex gap-2 ${mine ? "justify-end" : "justify-start"} ${последнийВГруппе ? "mb-2" : "mb-0.5"}`}>
              {/* Фото собеседника — только у последнего в группе: в образце
                  подряд идущие сообщения выглядят одним блоком. */}
              {!mine && сИменами && (
                <span className="w-7 shrink-0 self-end">
                  {последнийВГруппе && <UserAvatar name={m.sender_name} color="var(--primary)" size={28} />}
                </span>
              )}
              <div
                onContextMenu={(e) => { e.preventDefault(); setMenu({ m, x: e.clientX, y: e.clientY }); }}
                onDoubleClick={() => startReply(m)}
                className="max-w-[80%] cursor-default px-3 py-1.5 text-[12px] text-[var(--text)]"
                style={{
                  background: mine ? "var(--bubble-mine)" : "var(--surfaceElevated)",
                  // Хвостик: у последнего пузыря группы угол со стороны автора
                  // скруглён меньше — именно это и читается как «хвостик».
                  borderRadius: mine
                    ? `var(--radius-lg) var(--radius-lg) ${последнийВГруппе ? "4px" : "var(--radius-lg)"} var(--radius-lg)`
                    : `var(--radius-lg) var(--radius-lg) var(--radius-lg) ${последнийВГруппе ? "4px" : "var(--radius-lg)"}`,
                }}>
                {сИменами && !mine && первыйВГруппе && (
                  <span className="mb-0.5 block text-[11px] font-semibold" style={{ color: "var(--primary)" }}>
                    {m.sender_name}
                  </span>
                )}
                {rep && (
                  <div className="mb-1 border-l-2 border-[var(--primary)] pl-2 text-[11px] text-[var(--textMuted)]">
                    <div className="font-semibold">{rep.sender_id === currentUserId ? "Вы" : rep.sender_name}</div>
                    <div className="truncate">{rep.text || (rep.attachments.length ? "📎 вложение" : "")}</div>
                  </div>
                )}
                {m.text && (() => {
                  // Сообщение из одних эмодзи рисуем крупно (и анимацией, если она
                  // есть) — как в мессенджерах; обычный текст просто с картинками
                  // вместо системных значков.
                  const однимиЗначками = толькоЭмодзи(m.text);
                  return однимиЗначками
                    ? <КрупныеЭмодзи символы={однимиЗначками} />
                    : <span className="whitespace-pre-wrap break-words">{сЭмодзи(m.text, `с${m.id}`)}</span>;
                })()}
                <AttachmentList items={m.attachments} />
                {m.reactions.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {m.reactions.map((r) => (
                      <button key={r.emoji} type="button" onClick={() => void reactTo(m, r.emoji)}
                        className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] leading-none transition"
                        style={{
                          background: r.mine ? "color-mix(in srgb, var(--primary) 22%, transparent)" : "color-mix(in srgb, var(--textMuted) 16%, transparent)",
                          border: r.mine ? "1px solid color-mix(in srgb, var(--primary) 55%, transparent)" : "1px solid transparent",
                          boxShadow: "none",
                        }}>
                        <span>{r.emoji}</span>
                        <span style={{ color: "var(--textMuted)" }}>{r.count}</span>
                      </button>
                    ))}
                  </div>
                )}
                {/* Время и галочки — внутри пузыря справа снизу, как в образце. */}
                <span className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-[var(--textMuted)]">
                  {m.edited_at ? "изм. " : ""}
                  {new Date(m.created_at + "Z").toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                  {mine && !сИменами && (
                    <span data-tooltip={m.read_at ? "Прочитано" : "Отправлено"}>{m.read_at ? "✓✓" : "✓"}</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );

  const наборная = (подпись: string) => (
        <div className="border-t border-[var(--border)] p-2.5">
          {(replyTo || editing) && (
            <div className="mb-2 flex items-center gap-2 rounded-[var(--radius-lg)] border-l-2 border-[var(--primary)] bg-[var(--surfaceElevated)] px-2.5 py-1.5 text-[11px]">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[var(--primary)]">{editing ? "Редактирование" : `Ответ: ${replyTo!.sender_id === currentUserId ? "Вы" : replyTo!.sender_name}`}</div>
                <div className="truncate text-[var(--textMuted)]">{(editing ?? replyTo)!.text || "📎 вложение"}</div>
              </div>
              <button type="button" onClick={() => { setReplyTo(null); setEditing(null); setText(""); }} className="text-[var(--textMuted)] hover:text-[var(--danger)]"><IconClose size={14} /></button>
            </div>
          )}
          {(pendingFiles.length > 0 || linkOpen) && (
            <div className="mb-2 space-y-1.5">
              {pendingFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 rounded-[var(--radius-lg)] bg-[var(--surfaceElevated)] px-2 py-1 text-[11px] text-[var(--text)]">
                  <FileText size={12} /><span className="flex-1 truncate">{f.name}</span>
                  <button type="button" onClick={() => setPendingFiles((p) => p.filter((_, j) => j !== i))} className="text-[var(--textMuted)] hover:text-[var(--danger)]"><IconClose size={12} /></button>
                </div>
              ))}
              {linkOpen && (
                <input className={inputCls} placeholder="https://ссылка…" value={linkVal}
                  onChange={(e) => setLinkVal(e.target.value)} autoFocus />
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <input ref={fileRef} type="file" multiple className="hidden"
              onChange={(e) => { const fs = Array.from(e.target.files ?? []); setPendingFiles((p) => [...p, ...fs]); if (fileRef.current) fileRef.current.value = ""; }} />
            <button type="button" onClick={() => fileRef.current?.click()} data-tooltip="Прикрепить файл/медиа"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--textMuted)] hover:text-[var(--text)]"><IconAttach size={16} /></button>
            <button type="button" onClick={() => setLinkOpen((v) => !v)} data-tooltip="Прикрепить ссылку"
              className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full hover:text-[var(--text)] ${linkOpen ? "seg seg-on" : "seg"}`}><IconLink size={16} /></button>
            {/* Эмодзи: разделы, недавние и ~330 значков (lib/emoji.ts). Шесть
                штук в ряд, как было, оказались «скудным набором» (Максим,
                04.09.2026) — с клавиатуры их в чате никто не набирает.
                Окно не закрываем после выбора: подряд ставят по два-три. */}
            <span className="relative shrink-0">
              <button type="button" onClick={() => setEmojiOpen((v) => !v)} data-tooltip="Эмодзи"
                className={`inline-flex h-9 w-9 items-center justify-center rounded-full hover:text-[var(--text)] ${emojiOpen ? "seg seg-on" : "seg"}`}><Smile size={16} /></button>
              {emojiOpen && (
                <EmojiPicker onPick={(символ) => setText((v) => v + символ)} onClose={() => setEmojiOpen(false)} />
              )}
            </span>
            <input className={inputCls} placeholder={подпись} value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} />
            <VoiceButton onText={(t) => setText((v) => (v ? v.trimEnd() + " " : "") + t)} />
            <button type="button" onClick={() => void send()} disabled={!text.trim() && pendingFiles.length === 0 && !linkVal.trim()}
              className="glass-btn-primary inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-[var(--primaryText)] disabled:opacity-50">
              <IconSend size={15} />
            </button>
          </div>
        </div>
  );

  // ── Канал ────────────────────────────────────────────────────────────────
  const видКанала = !channel ? null : (() => {
    const закреплённое = messages.find((m) => m.id === channel.pinned_message_id) || null;
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2">
          <button type="button" onClick={() => { setChannel(null); setChInfo(null); setInfoOpen(false); void loadUsers(); }}
                  className="text-[var(--textMuted)] hover:text-[var(--text)]"><ArrowLeft size={16} /></button>
          <span className="min-w-0 flex-1 truncate">
            <span className="text-[12px] font-semibold text-[var(--text)]">
              {channel.is_private ? "🔒" : "#"} {channel.name}
            </span>
            <span className="ml-1 text-[11px] text-[var(--textMuted)]">· {channel.members} участн.</span>
          </span>
          {/* Ряд значков справа — как в образце. Звонка и видео в портале нет
              вовсе, поэтому их и не рисуем: кнопка, которая ничего не делает,
              хуже отсутствующей. Оставлены те, за которыми есть действие. */}
          <button type="button" data-tooltip={закреплённое ? "Открепить сообщение" : "Закрепить: выберите сообщение в ленте"}
                  onClick={async () => {
                    if (!закреплённое) return;
                    try { await api.tmChannelPin(channel.id, 0); setChannel({ ...channel, pinned_message_id: null }); } catch { /* тихо */ }
                  }}
                  className="glass-flat shrink-0 text-[var(--textMuted)] hover:text-[var(--text)]">
            <Pin size={15} />
          </button>
          <button type="button" data-tooltip={channel.archived_at ? "Вернуть из архива" : "Убрать канал в архив"}
                  onClick={async () => {
                    try {
                      const r = await api.tmChannelArchive(channel.id, !channel.archived_at);
                      setChannel(null); setInfoOpen(false);
                      await loadUsers(); if (r.archived || архив) await loadArchived();
                    } catch { /* тихо */ }
                  }}
                  className="glass-flat shrink-0 text-[var(--textMuted)] hover:text-[var(--text)]">
            <Archive size={15} />
          </button>
          <button type="button" data-tooltip="Информация о канале"
                  onClick={async () => {
                    setInfoOpen((v) => !v);
                    if (!chInfo) { try { setChInfo(await api.tmChannelInfo(channel.id)); } catch { /* тихо */ } }
                  }}
                  className="glass-flat shrink-0 text-[var(--textMuted)] hover:text-[var(--text)]">
            <Info size={15} />
          </button>
        </div>

        {/* Закреплённое — полосой сверху, как в образце. */}
        {закреплённое && !infoOpen && (
          <div className="flex items-start gap-2 border-b border-[var(--border)] px-3 py-2"
               style={{ background: "color-mix(in srgb, var(--primary) 8%, transparent)" }}>
            <Pin size={13} className="mt-0.5 shrink-0 text-[var(--primary)]" />
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] font-semibold text-[var(--primary)]">Закреплённое сообщение</span>
              <span className="block truncate text-[11px] text-[var(--text)]">{закреплённое.text}</span>
            </span>
            <button type="button" data-tooltip="Открепить" className="glass-flat shrink-0 text-[var(--textMuted)]"
                    onClick={async () => { try { await api.tmChannelPin(channel.id, 0); setChannel({ ...channel, pinned_message_id: null }); } catch { /* тихо */ } }}>
              <IconClose size={13} />
            </button>
          </div>
        )}

        {infoOpen ? (
          <ИнформацияОКанале info={chInfo} onClose={() => setInfoOpen(false)} />
        ) : (
          <>
            {лента("В канале пока пусто", "Напишите первым — сообщение увидят все участники.", true)}
            {наборная(`Написать в #${channel.name}…`)}
          </>
        )}
      </div>
    );
  })();

  const видЛички = !active ? null : (() => {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2">
          <button type="button" onClick={() => { setActive(null); void loadUsers(); }} className="text-[var(--textMuted)] hover:text-[var(--text)]"><ArrowLeft size={16} /></button>
          <span className="relative shrink-0">
            <UserAvatar name={active.name} color={active.color || "var(--primary)"} url={active.avatar_url} size={28} />
            {active.online && <span data-tooltip="В сети" className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-[var(--surface)]" style={{ background: "#22c55e" }} />}
          </span>
          <span className="min-w-0 flex-1 truncate">
            <span className="text-[12px] font-semibold text-[var(--text)]">{active.name}</span>
            {active.online ? <span className="ml-1 text-[11px]" style={{ color: "var(--primary)" }}>· в сети</span>
              : active.position ? <span className="ml-1 text-[11px] text-[var(--textMuted)]">· {active.position}</span> : null}
          </span>
          {onAiSummarize && messages.some((m) => m.text) && (
            <button type="button" data-tooltip="Краткое резюме переписки (AI)"
              onClick={() => {
                const lines = messages.filter((m) => m.text).slice(-50).map((m) => `${m.sender_id === currentUserId ? "Я" : active.name}: ${m.text}`);
                if (!lines.length) return;
                onAiSummarize(`Кратко резюмируй эту переписку с ${active.name}${active.position ? ` (${active.position})` : ""}: о чём договорились, что решено и что от меня ждут. Ответь короткими пунктами.\n\nПереписка:\n${lines.join("\n")}`);
              }}
              className="inline-flex shrink-0 items-center gap-1 rounded-[var(--radius-md)] border px-2 py-0.5 text-[11px] font-semibold transition"
              style={{ borderColor: `color-mix(in srgb, ${AI_COLOR} 45%, transparent)`, color: `color-mix(in srgb, ${AI_COLOR} 72%, var(--text))`, boxShadow: "none" }}>
              <IconSparkles size={12} /> Кратко
            </button>
          )}
        </div>
        {лента("Сообщений пока нет", "Напишите первым — сообщение придёт коллеге сразу.", false)}
        {наборная("Сообщение…")}
        {menu && (() => {
          const mine = menu.m.sender_id === currentUserId;
          const MW = 200;
          const left = Math.max(8, Math.min(menu.x, window.innerWidth - MW - 10));
          const top = Math.max(8, Math.min(menu.y, window.innerHeight - 210));
          const Item = ({ icon, label, onClick, danger }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) => (
            <button type="button" onClick={onClick}
              className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] transition glass-hover ${danger ? "text-[var(--danger)]" : "text-[var(--text)]"}`}>
              {icon}{label}
            </button>
          );
          return (
            <>
              <div className="fixed inset-0" style={{ zIndex: Z.DROPDOWN - 1 }} onClick={() => setMenu(null)} onContextMenu={(e) => { e.preventDefault(); setMenu(null); }} />
              <div style={{ position: "fixed", left, top, zIndex: Z.DROPDOWN }}
                className="w-[200px] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] py-1 elev-modal">
                {/* ряд быстрых реакций */}
                <div className="flex items-center justify-between border-b border-[var(--border)] px-2 py-1.5">
                  {["❤️", "👍", "🔥", "😂", "👌", "💯"].map((e) => (
                    <button key={e} type="button" onClick={() => void reactTo(menu.m, e)}
                      className="rounded-[var(--radius-md)] px-1 text-[12px] leading-none transition hover:scale-125" style={{ boxShadow: "none" }}>
                      {e}
                    </button>
                  ))}
                </div>
                <Item icon={<CornerUpLeft size={15} className="text-[var(--textMuted)]" />} label="Ответить" onClick={() => startReply(menu.m)} />
                {menu.m.text && <Item icon={<Copy size={15} className="text-[var(--textMuted)]" />} label="Копировать текст" onClick={() => { void navigator.clipboard?.writeText(menu.m.text); setMenu(null); }} />}
                {mine && <Item icon={<IconEdit size={15} className="text-[var(--textMuted)]" />} label="Редактировать" onClick={() => startEdit(menu.m)} />}
                {mine && <Item icon={<IconTrash size={15} />} label="Удалить" danger onClick={() => void delMsg(menu.m)} />}
              </div>
            </>
          );
        })()}
      </div>
    );
  })();

  const q = query.trim().toLowerCase();
  const shownUsers = q ? users.filter((u) => u.name.toLowerCase().includes(q) || (u.position || "").toLowerCase().includes(q)) : users;
  const shownChannels = q ? channels.filter((c) => c.name.toLowerCase().includes(q) || (c.title || "").toLowerCase().includes(q)) : channels;
  const бесед_видно = q ? беседы.filter((c) => (c.title || "").toLowerCase().includes(q)) : беседы;
  const список = (
    <div className="p-2">
      {/* Шапка списка — как в образце: название раздела и перо «новый разговор».
          Перо ведёт к созданию канала: личный разговор начинается кликом по
          человеку ниже, отдельного шага для него не нужно. */}
      <div className="mb-2 flex items-center gap-2 px-1.5">
        <span className="text-[12px] font-semibold text-[var(--text)]">Внутренний чат</span>
        {/* ⚠️ КНОПКА РАЗВОРОТА — ЗДЕСЬ, а не отдельной полосой. Когда поиск
            уехал в колонку, наверху панели осталась пустая лента ради одной
            этой кнопки (Максим, 03.09.2026: «зачем нам целиком такая строка?»).
            Место ей рядом с пером: обе про то, как выглядит список. */}
        <button type="button" data-tooltip="Новый канал" onClick={() => setCreatingCh(true)}
                className="glass-flat ml-auto text-[var(--textMuted)] hover:text-[var(--text)]">
          <IconEdit size={14} />
        </button>
        {onToggleWide && (
          <button type="button" onClick={onToggleWide}
                  data-tooltip={wide ? "Свернуть панель" : "Развернуть панель"}
                  aria-label={wide ? "Свернуть панель" : "Развернуть панель"}
                  className="glass-flat hidden text-[var(--textMuted)] hover:text-[var(--text)] sm:inline-flex">
            {wide ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        )}
      </div>
      <div className="mb-2 flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5">
        <IconSearch size={13} className="shrink-0 text-[var(--textMuted)]" />
        <input value={query} onChange={(e) => onQueryChange?.(e.target.value)}
               placeholder="Поиск…" aria-label="Поиск по чатам"
               className="min-w-0 flex-1 bg-transparent text-[12px] text-[var(--text)] placeholder:text-[var(--textMuted)] focus:outline-none" />
        {query && (
          <button type="button" onClick={() => onQueryChange?.("")} aria-label="Очистить"
                  className="shrink-0 text-[var(--textMuted)] hover:text-[var(--text)]"><IconClose size={13} /></button>
        )}
      </div>
      {/* ⚠️ АССИСТЕНТ — ЗАКРЕПЛЁННОЙ СТРОКОЙ НАД ВСЕМ (Максим, 03.09.2026:
          «поместить в общий список, но отделить, чтобы был закреплён»). Он и
          есть собеседник, просто не человек, — и искать его во вкладках внизу
          неудобно. Отделён чертой и значком булавки: строка не уезжает вниз
          при фильтрах и не участвует в поиске по имени. */}
      {onOpenAi && (
        <div className="mb-2 border-b border-[var(--border)] pb-2">
          <button type="button" onClick={() => { setActive(null); setChannel(null); setСрез("ai"); setАиБеседа("последняя"); }}
                  className="data-row flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-left">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full"
                  style={{ background: "color-mix(in srgb, var(--primary) 18%, transparent)" }}>
              <IconSparkles size={14} style={{ color: "var(--primary)" }} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12px] font-semibold text-[var(--text)]">Ассистент</span>
              <span className="block truncate text-[11px] text-[var(--textMuted)]">Спросить о данных портала</span>
            </span>
            <Pin size={12} className="shrink-0 text-[var(--textMuted)]" />
          </button>
        </div>
      )}

      {/* Вкладки среза — как в образце. «Все» по умолчанию: в маленькой компании
          разговоров немного, и разделять их каждый раз незачем.
          ⚠️ ОДНОЙ СТРОКОЙ ВСЕГДА (Максим, 04.09.2026). Перенос на вторую строку
          разрывал ряд пополам — «AI» уезжал под остальные. Поэтому: плашки не
          переносятся и не сжимаются, боковые поля уменьшены, а если окно совсем
          узкое — ряд прокручивается вбок, полоса прокрутки скрыта. */}
      <div className="mb-2 flex flex-nowrap items-center gap-1 overflow-x-auto px-1.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {([["all", "Все"], ["channels", "Каналы"], ["dm", "Личные"], ["ai", "AI"]] as const).map(([k, подпись]) => {
          const вкл = срез === k;
          return (
            <button key={k} type="button" onClick={() => setСрез(k)}
              className={`glass-flat shrink-0 whitespace-nowrap rounded-[var(--radius-md)] border px-2 py-1 text-[11px] font-semibold ${
                вкл ? "border-transparent" : "border-[var(--border)] text-[var(--textMuted)]"}`}
              style={вкл ? { background: "var(--primary)", color: "var(--primaryText)" } : undefined}>
              {подпись}
            </button>
          );
        })}
      </div>

      {/* ── Срез «AI»: беседы с ассистентом тем же списком, что каналы и люди ──
          Строка ведёт во вкладку ассистента и сразу открывает выбранную беседу,
          поэтому переписка не теряется между вкладками. */}
      {срез === "ai" && (
        <>
          {/* Кнопка во всю ширину колонки: у <button> ширина по содержимому даже
              при display:flex, поэтому w-full и обёртка с боковыми полями. */}
          <div className="mb-1.5">
            <button type="button" onClick={() => { setActive(null); setChannel(null); setАиБеседа(`новая-${Date.now()}`); }}
              className="glass-flat flex w-full items-center justify-center gap-1.5 rounded-[var(--radius-md)] border px-3 py-1 text-[12px] font-semibold transition-all"
              style={{ borderColor: "color-mix(in srgb, var(--primary) 55%, transparent)", color: "color-mix(in srgb, var(--primary) 70%, var(--text))" }}>
              <IconPlus size={13} /> Новый чат
            </button>
          </div>
          <div className="mb-1 px-1.5"><span className="field-label">Беседы с ассистентом</span></div>
          {бесед_видно.length === 0 && (
            <p className="px-1.5 pb-2 text-[11px] text-[var(--textMuted)]">
              {q ? "Бесед по запросу нет." : "Бесед пока нет — начните плюсом справа."}
            </p>
          )}
          <ul className="mb-2 flex flex-col">
            {бесед_видно.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => { setActive(null); setChannel(null); setАиБеседа(c.id); }}
                        className="data-row flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-left">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full"
                        style={{ background: "color-mix(in srgb, var(--primary) 18%, transparent)" }}>
                    <IconSparkles size={14} style={{ color: "var(--primary)" }} />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--text)]">{c.title || "Новая беседа"}</span>
                  <span className="shrink-0 text-[11px] text-[var(--textMuted)]">{когдаВСписке(c.updated_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* ⚠️ КАНАЛЫ ВЫШЕ ЛИЧНЫХ — как в образце: сначала общие разговоры, потом
          переписка один на один. Закрытый помечен замком: по значку сразу
          видно, кто его читает. */}
      <div className={`mb-1 flex items-center gap-2 px-1.5 ${срез === "dm" || срез === "ai" ? "hidden" : ""}`}>
        <span className="field-label">Каналы</span>
        <button type="button" data-tooltip="Создать канал" onClick={() => setCreatingCh(true)}
                className="glass-flat ml-auto text-[var(--textMuted)] hover:text-[var(--text)]">
          <IconPlus size={13} />
        </button>
      </div>
      {shownChannels.length === 0 && срез !== "dm" && срез !== "ai" && (
        <p className="px-1.5 pb-2 text-[11px] text-[var(--textMuted)]">
          {q ? "Каналов по запросу нет." : "Каналов пока нет — заведите первый плюсом справа."}
        </p>
      )}
      <ul className={`mb-2 flex flex-col ${срез === "dm" || срез === "ai" ? "hidden" : ""}`}>
        {shownChannels.map((c) => (
          <li key={c.id}>
            {/* ⚠️ СТРОКА КАНАЛА ОДНОСТРОЧНАЯ, как в образце: решётка, имя и
                счётчик. Я сначала дал ей кружок и подпись, как у человека, —
                и ряд каналов стал тяжелее списка людей, хотя в образце он
                наоборот легче (Максим, 03.09.2026). */}
            <button type="button" onClick={() => { setАиБеседа(null); setChannel(c); setChInfo(null); void loadChannel(c.id); }}
                    className="data-row flex w-full items-center gap-1.5 rounded-[var(--radius-md)] px-2 py-1.5 text-left">
              {c.is_private ? <Lock size={13} className="shrink-0 text-[var(--textMuted)]" />
                            : <Hash size={13} className="shrink-0 text-[var(--textMuted)]" />}
              <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[var(--text)]">{c.name}</span>
              {c.unread > 0 && (
                <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold"
                      style={{ background: "var(--primary)", color: "var(--primaryText)" }}>{c.unread}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {/* «Архив каналов» — как в образце, в конце списка. Открывается по клику
          и показывает убранные каналы; переписка в них цела. */}
      <div className={`mb-2 ${срез === "dm" || срез === "ai" ? "hidden" : ""}`}>
        <button type="button"
          onClick={() => { setАрхив((v) => !v); if (!архивные.length) void loadArchived(); }}
          className="data-row flex w-full items-center gap-1.5 rounded-[var(--radius-md)] px-2 py-1.5 text-left text-[11px] text-[var(--textMuted)]">
          <Archive size={13} className="shrink-0" />
          <span className="flex-1">архив каналов</span>
          {архив && архивные.length > 0 && <span>{архивные.length}</span>}
        </button>
        {архив && (
          <ul className="flex flex-col">
            {!архивные.length && <li className="px-2 py-1 text-[11px] text-[var(--textMuted)]">Архив пуст.</li>}
            {архивные.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => { setАиБеседа(null); setChannel(c); setChInfo(null); void loadChannel(c.id); }}
                        className="data-row flex w-full items-center gap-1.5 rounded-[var(--radius-md)] px-2 py-1.5 text-left opacity-70">
                  <Hash size={13} className="shrink-0 text-[var(--textMuted)]" />
                  <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--text)]">{c.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={`mb-1 px-1.5 ${срез === "channels" || срез === "ai" ? "hidden" : ""}`}><span className="field-label">Личные сообщения</span></div>
      {срез !== "channels" && срез !== "ai" && shownUsers.length === 0 && (q
        ? <EmptyState icon={<IconSearch size={20} />} title="Ничего не найдено" hint="Попробуйте другое имя или должность." />
        : <EmptyState icon={<Users size={20} />} title="Нет сотрудников" hint="Здесь появятся коллеги, которым можно написать." accent="var(--primary)" />)}
      <ul className={`flex flex-col ${срез === "channels" || срез === "ai" ? "hidden" : ""}`}>
        {shownUsers.map((u) => (
          <RosterRow key={u.id} u={u} onOpen={() => { setАиБеседа(null); setActive(u); void loadThread(u.id); }} />
        ))}
      </ul>

      {creatingCh && (
        <СозданиеКанала onClose={() => setCreatingCh(false)}
                        onCreated={async () => { setCreatingCh(false); await loadUsers(); }} />
      )}
    </div>
  );

  // ⚠️ ТРИ КОЛОНКИ, КАК В ОБРАЗЦЕ — в развёрнутой панели. Максим прислал
  // сравнение (03.09.2026): у образца список, переписка и сведения стоят РЯДОМ
  // в одной панели шириной около 720 px — ровно наша развёрнутая. В свёрнутой
  // (420 px) колонки вышли бы по 140 px и стали бы нечитаемыми, поэтому там
  // остаётся прежнее переключение: список ИЛИ переписка.
  const видАссистента = аиБеседа ? (
    <AiTab встроенный openConvId={аиБеседа} onConvOpened={() => { /* номер держим: он же признак «открыта беседа» */ }}
           onBack={() => setАиБеседа(null)} wide={wide} onToggleWide={onToggleWide} />
  ) : null;
  const переписка = видАссистента ?? видКанала ?? видЛички;
  if (wide) {
    return (
      <div className="flex h-full min-h-0">
        <aside className="w-[210px] shrink-0 overflow-y-auto border-r border-[var(--border)]">{список}</aside>
        <section className="flex min-w-0 flex-1 flex-col">
          {переписка ?? (
            <div className="flex h-full items-center justify-center px-6">
              <EmptyState icon={<MessagesSquare size={20} />} title="Выберите разговор"
                          hint="Слева каналы и личные сообщения. Откройте любой — переписка появится здесь."
                          accent="var(--primary)" />
            </div>
          )}
        </section>
      </div>
    );
  }
  return переписка ?? список;
}

// Строка ростера: при наведении светится цветом отдела пользователя
/** Время в списке: сегодня — часами, вчера — словом, дальше — датой.
 *  Так же читается список в любом мессенджере. */
function когдаВСписке(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return "";
  const сейчас = new Date();
  if (d.toDateString() === сейчас.toDateString()) {
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  }
  const вчера = new Date(сейчас); вчера.setDate(вчера.getDate() - 1);
  if (d.toDateString() === вчера.toDateString()) return "вчера";
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
}

// ⚠️ СТРОКА СПИСКА — КАК В ПРИВЫЧНОМ МЕССЕНДЖЕРЕ (Максим, 03.09.2026: «разве
// это выглядит как на образце?»). Было: имя и ДОЛЖНОСТЬ — то есть строка
// отвечала «кто это», хотя человек пришёл узнать «чем кончился разговор».
// Стало: последнее сообщение с пометкой «Вы:» и время справа. Должность
// осталась там, где её и ищут, — в профиле и в подсказке.
function RosterRow({ u, onOpen }: { u: ChatUser; onOpen: () => void }) {
  const c = u.color || "var(--primary)";
  return (
    <li>
      {/* ⚠️ СТРОКА ПЛОТНАЯ, как в образце: фото 28 и отступы вдвое меньше. С
          фото 36 и просторными полями в колонку влезало вдвое меньше людей, а
          имена обрезались на середине (Максим, 03.09.2026). */}
      <button type="button" onClick={onOpen} data-tooltip={u.position || undefined}
        className="data-row flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-left">
        <span className="relative shrink-0">
          <UserAvatar name={u.name} color={c} url={u.avatar_url} size={28} />
          {u.online && (
            <span data-tooltip="В сети" className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-[var(--surface)]" style={{ background: "#22c55e" }} />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12px] font-semibold text-[var(--text)]">{u.name}</span>
          <span className="block truncate text-[11px] text-[var(--textMuted)]">
            {u.last_text
              ? <>{u.last_mine && <span className="text-[var(--textMuted)]">Вы: </span>}{u.last_text}</>
              : (u.position || "")}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-[11px] text-[var(--textMuted)]">{когдаВСписке(u.last_at)}</span>
          {u.unread > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--primary)] px-1.5 text-[11px] font-semibold text-[var(--primaryText)]">{u.unread}</span>
          )}
        </span>
      </button>
    </li>
  );
}

// ── Задачи между сотрудниками ────────────────────────────────────────────────
// колонки доски задач — служат и фильтром, и подписями кнопок
type ColKey = "all" | "new" | "in_progress" | "review" | "done" | "backlog";
const TASK_COLS: { v: ColKey; l: string; c: string }[] = [
  { v: "new", l: "Новые", c: "var(--primary)" },
  { v: "in_progress", l: "В работе", c: "#d97706" },
  { v: "review", l: "Согласование", c: "#8b5cf6" },
  { v: "done", l: "Готово", c: "#16a34a" },
  { v: "backlog", l: "Бэклог", c: "#64748b" },
];

function TasksTab({ isAdmin, query = "", onSubview, autoNew }: { isAdmin: boolean; query?: string; onSubview?: (v: boolean) => void; autoNew?: boolean }) {
  void isAdmin;
  const [col, setCol] = useState<ColKey>("new");  // выбранный столбец доски
  const [tasks, setTasks] = useState<TeamTask[]>([]);
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [creating, setCreating] = useState(!!autoNew);  // веер «Новая задача» → сразу форма
  const [openTask, setOpenTask] = useState<TeamTask | null>(null);  // открытая задача (полная панель внутри FAB)
  // открытая задача = под-экран (прячем глобальный поиск)
  useEffect(() => { onSubview?.(!!openTask); return () => onSubview?.(false); }, [openTask, onSubview]);

  // Показываем И назначенные мне, И поставленные мной. Раньше грузился только
  // "outbox", а бейдж на кнопке считает задачи, назначенные МНЕ («Новая
  // задача») — человек шёл по уведомлению и не находил ту самую задачу.
  async function load() {
    try {
      const [inbox, outbox] = await Promise.all([
        api.listTasks("inbox").catch(() => [] as TeamTask[]),
        api.listTasks("outbox").catch(() => [] as TeamTask[]),
      ]);
      const seen = new Set<number>();
      setTasks([...inbox, ...outbox].filter((t) => !seen.has(t.id) && seen.add(t.id)));
    } catch { /* ignore */ }
  }
  useEffect(() => { void load(); }, []);
  useEffect(() => { void (async () => { try { setUsers(await api.chatUsers()); } catch { /* ignore */ } })(); }, []);

  async function remove(id: number) {
    try { await api.deleteTask(id); await load(); } catch { /* ignore */ }
  }

  const tq = query.trim().toLowerCase();
  const shownTasks = tq
    ? tasks.filter((t) => `${t.title} ${t.description} ${t.assignee_name} ${t.author_name}`.toLowerCase().includes(tq))
    : tasks;
  const topTasks = shownTasks.filter((t) => !t.parent_task_id);
  const colCounts: Record<string, number> = {};
  for (const t of topTasks) colCounts[t.status] = (colCounts[t.status] ?? 0) + 1;
  const visTasks = col === "all" ? topTasks : topTasks.filter((t) => t.status === col);

  // компактная карточка задачи (как на доске): кантик по приоритету + заголовок
  // + подпись (автор · подзадачи · срок); клик раскрывает полную панель.
  const renderTask = (t: TeamTask) => {
    const accent = prioColor(t.priority);
    const children = tasks.filter((x) => x.parent_task_id === t.id);
    const childDone = children.filter((c) => c.status === "done").length;
    const sub = [
      t.author_name ? `от ${t.author_name}` : "",
      children.length ? `подзадачи ${childDone}/${children.length}` : "",
      t.due_date ? `до ${t.due_date}` : "",
    ].filter(Boolean).join(" · ");
    return (
      <li key={t.id} onClick={() => setOpenTask(t)} data-tooltip="Открыть задачу"
        className="panel-elevated group flex cursor-pointer items-center gap-2 px-2.5 py-2 transition-all hover:-translate-y-px"
        style={{ borderLeftWidth: 3, borderLeftColor: accent }}>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12px] font-semibold text-[var(--text)]" >{t.title}</p>
          {sub && <p className="truncate text-[11px] text-[var(--textMuted)]">{sub}</p>}
        </div>
        <button type="button" onClick={(e) => { e.stopPropagation(); void remove(t.id); }} aria-label="Удалить"
          className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-full text-[var(--textMuted)] hover:text-[var(--danger)] group-hover:inline-flex">
          <IconTrash size={14} />
        </button>
      </li>
    );
  };

  if (openTask) {
    return (
      <div className="p-3">
        <TaskDetailPanel inline task={openTask} allTasks={tasks} canEdit
          onClose={() => setOpenTask(null)} onChanged={() => void load()}
          onOpenTask={(child) => setOpenTask(child)} onPickParent={() => {}}
          onDelete={() => { void (async () => { try { for (const c of tasks.filter((x) => x.parent_task_id === openTask.id)) await api.deleteTask(c.id); await api.deleteTask(openTask.id); setOpenTask(null); await load(); } catch { /* ignore */ } })(); }} />
      </div>
    );
  }

  return (
    <div className="p-3">
      {/* кнопки = столбцы доски задач; одна строка с гориз. прокруткой, фильтруют мои задачи */}
      <div className="mb-3 flex items-center gap-1.5">
        <div className="scrollbar-autohide flex min-w-0 flex-1 gap-1 overflow-x-auto">
          {TASK_COLS.map((b) => {
            const active = col === b.v;
            const n = b.v === "all" ? topTasks.length : (colCounts[b.v] ?? 0);
            return (
              <button key={b.v} type="button" onClick={() => setCol(b.v)}
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-[var(--radius-md)] px-2 py-0.5 text-[11px] font-semibold transition"
                style={active
                  ? { background: b.c, color: "#fff", boxShadow: `0 0 12px -4px ${b.c}` }
                  : { background: "var(--surfaceElevated)", color: "var(--textMuted)" }}>
                {b.l}
                {n > 0 && (
                  <span className="inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-1 text-[11px] font-semibold leading-none"
                    style={active ? { background: "rgba(255,255,255,.25)", color: "#fff" } : { background: "var(--surface)", color: "var(--textMuted)" }}>{n}</span>
                )}
              </button>
            );
          })}
        </div>
        <button type="button" onClick={() => setCreating(true)} data-tooltip="Новая задача"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surfaceElevated)] text-[var(--text)]">
          <IconPlus size={15} />
        </button>
      </div>

      {creating && (
        <TaskCreatePanel users={users} onClose={() => setCreating(false)} onCreated={() => void load()} />
      )}

      {visTasks.length === 0 && (query.trim()
        ? <EmptyState icon={<IconSearch size={20} />} title="Ничего не найдено" hint="Попробуйте изменить запрос или столбец." />
        : <EmptyState icon={<ClipboardList size={20} />} title={col === "all" ? "Задач нет" : "В этом столбце пусто"} hint="Нажмите + справа, чтобы поставить задачу коллеге." accent="var(--primary)" />)}

      {visTasks.length > 0 && (
        <ul className="flex flex-col gap-2">{visTasks.map(renderTask)}</ul>
      )}
    </div>
  );
}

// ── AI-чат (GPT через Codex-прокси) ───────────────────────────────────────────
// Акцент AI-ассистента — основной цвет портала (был розовый #ff5cc8: панель
// выбивалась из общей палитры сильнее всего).
const AI_COLOR = "var(--primary)";

// Стартовые подсказки в пустом чате — точки входа в 93-статейную базу знаний,
// методички вкладок и личные данные («Мои задачи» дёргает [[FETCH:my_tasks]]).
const STARTER_CHIPS = [
  "Что нового за сегодня?",
  "С чего начать работу в портале?",
  "Как считается маржа?",
  "Что показывает вкладка, которую я открыл?",
];

// Маркер перехода [[NAV:/путь|Метка]] в ответе ассистента → кнопка-переход.
// Вырезаем маркеры из текста и отдаём список назначений (только внутренние пути «/…»).
const NAV_RE = /\[\[NAV:([^\]|]+)(?:\|([^\]]+))?\]\]/g;
function extractNav(text: string): { clean: string; navs: { path: string; label: string }[] } {
  const navs: { path: string; label: string }[] = [];
  const clean = text.replace(NAV_RE, (_m, path: string, label?: string) => {
    const p = (path || "").trim();
    if (p.startsWith("/")) navs.push({ path: p, label: (label || p).trim() });
    return "";
  }).replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  return { clean, navs };
}

type StreamState = { reasoning: string; content: string; image_url: string | null };

// Инлайн-markdown: **жирный** и `код`. Возвращает массив React-узлов.
function mdInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0, m: RegExpExecArray | null, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2] !== undefined) out.push(<strong key={`${keyPrefix}-b${i}`}>{m[2]}</strong>);
    else if (m[3] !== undefined) out.push(<code key={`${keyPrefix}-c${i}`} className="rounded-[var(--radius-md)] bg-[var(--surface)] px-1 py-0.5 text-[0.85em] font-mono">{m[3]}</code>);
    last = m.index + m[0].length; i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Лёгкий markdown-рендер для ответов ассистента: код-блоки ```, заголовки #,
// списки -/1., жирный **, инлайн-код `. Без внешних зависимостей.
function ChatMarkdown({ text }: { text: string }) {
  const blocks = text.split(/(```[\s\S]*?```)/g).filter((b) => b !== "");
  return (
    <div className="space-y-1.5 break-words">
      {blocks.map((block, bi) => {
        if (block.startsWith("```")) {
          const code = block.replace(/^```[^\n]*\n?/, "").replace(/\n?```$/, "");
          return <pre key={bi} className="overflow-x-auto rounded-[var(--radius-md)] bg-[var(--surface)] p-2 text-[11px] leading-snug font-mono">{code}</pre>;
        }
        const lines = block.split("\n");
        const nodes: React.ReactNode[] = [];
        let list: React.ReactNode[] | null = null;
        const flush = () => { if (list) { nodes.push(<ul key={`u${nodes.length}`} className="ml-4 list-disc space-y-0.5">{list}</ul>); list = null; } };
        lines.forEach((ln, li) => {
          const h = ln.match(/^(#{1,3})\s+(.*)$/);
          const bullet = ln.match(/^\s*[-*]\s+(.*)$/);
          const num = ln.match(/^\s*\d+\.\s+(.*)$/);
          if (h) { flush(); nodes.push(<div key={li} className="mt-1 font-semibold">{mdInline(h[2], `h${li}`)}</div>); }
          else if (bullet) { (list ??= []).push(<li key={li}>{mdInline(bullet[1], `l${li}`)}</li>); }
          else if (num) { (list ??= []).push(<li key={li} className="list-decimal">{mdInline(num[1], `n${li}`)}</li>); }
          else if (ln.trim() === "") { flush(); }
          else { flush(); nodes.push(<p key={li}>{mdInline(ln, `p${li}`)}</p>); }
        });
        flush();
        return <div key={bi} className="space-y-1">{nodes}</div>;
      })}
    </div>
  );
}

function AiTab({ query = "", onSubview, wide, onToggleWide, currentLocation = "", onClose, autoNew, seedPrompt, onSeedConsumed, openConvId, onConvOpened, встроенный, onBack }: { /** Встроена в правую колонку чата: своего списка не рисует, «назад» отдаёт наружу. */ встроенный?: boolean; onBack?: () => void; openConvId?: number | string | null; onConvOpened?: () => void; query?: string; onSubview?: (v: boolean) => void; wide?: boolean; onToggleWide?: () => void; currentLocation?: string; onClose?: () => void; autoNew?: boolean; seedPrompt?: string | null; onSeedConsumed?: () => void }) {
  const router = useRouter();
  const [convs, setConvs] = useState<AiConversation[]>([]);
  const [active, setActive] = useState<{ id: number; title: string; messages: AiMessage[] } | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [stream, setStream] = useState<StreamState | null>(null);
  const [thinkOpen, setThinkOpen] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingConv, setLoadingConv] = useState(false);
  const [attachment, setAttachment] = useState<{ url: string; preview: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [renaming, setRenaming] = useState(false);   // правка заголовка беседы
  const [titleDraft, setTitleDraft] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);  // для кнопки «Стоп»

  // открытая беседа = под-экран (прячем глобальный поиск)
  useEffect(() => { onSubview?.(!!active); return () => onSubview?.(false); }, [active, onSubview]);

  async function handleFile(file: File | null | undefined) {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true); setError(null);
    try {
      const { url } = await api.aiUploadFile(file);
      setAttachment({ url, preview: URL.createObjectURL(file) });
    } catch { setError("Не удалось загрузить изображение"); }
    finally { setUploading(false); }
  }

  // Признак «список уже приезжал»: без него нельзя отличить «бесед нет» от
  // «ещё не загрузились», а от этого зависит, открывать новую беседу или ждать.
  const [convsLoaded, setConvsLoaded] = useState(false);
  async function loadConvs() {
    try { setConvs(await api.aiConversations()); } catch { /* ignore */ }
    finally { setConvsLoaded(true); }
  }
  useEffect(() => { void loadConvs(); }, []);
  // веер «Спросить AI» → сразу открыть новую беседу (один раз)
  const autoNewFired = useRef(false);
  useEffect(() => { if (autoNew && !autoNewFired.current) { autoNewFired.current = true; void newChat(); } }, [autoNew]);
  // Беседа, выбранная в списке чатов (срез «AI»): открываем и сбрасываем метку,
  // иначе следующий заход на вкладку снова утащит в ту же переписку.
  // ⚠️ КАЖДОЕ ЗАДАНИЕ ОТРАБАТЫВАЕТ РОВНО ОДИН РАЗ. Список бесед обновляется
  // после создания, а он же в зависимостях — без этой отметки «новая» заводила
  // бы беседу за беседой по кругу. Поэтому у нажатия «Новый чат» своя метка
  // времени: повторное нажатие — другое значение, значит сработает снова.
  const обработано = useRef<string | null>(null);
  useEffect(() => {
    if (!openConvId) { обработано.current = null; return; }
    const ключ = String(openConvId);
    if (обработано.current === ключ) return;
    if (typeof openConvId === "number") { обработано.current = ключ; void openConv(openConvId); onConvOpened?.(); return; }
    if (ключ.startsWith("новая")) { обработано.current = ключ; void newChat(); onConvOpened?.(); return; }
    // «последняя»: список приезжает запросом, поэтому ждём его — беседы уже
    // отсортированы свежими вверх. Бесед нет вовсе — заводим новую, чтобы по
    // нажатию на «Ассистента» сразу открывалось окно диалога (Максим, 04.09.2026).
    if (!convsLoaded) return;
    обработано.current = ключ;
    if (!convs.length) { void newChat(); onConvOpened?.(); return; }
    void openConv(convs[0].id);
    onConvOpened?.();
  }, [openConvId, convs, convsLoaded]);  // eslint-disable-line react-hooks/exhaustive-deps
  // «✨ Кратко» из чата → новая беседа с промптом-резюме (один раз)
  const seedFired = useRef(false);
  useEffect(() => { if (seedPrompt && !seedFired.current) { seedFired.current = true; void askNew(seedPrompt); onSeedConsumed?.(); } }, [seedPrompt]);  // eslint-disable-line react-hooks/exhaustive-deps
  // черновик: восстанавливаем недописанный вопрос при открытии беседы, сохраняем при вводе
  const draftConvId = useRef<number | null>(null);
  useEffect(() => {
    const id = active?.id ?? null;
    if (id === draftConvId.current) return;
    draftConvId.current = id;
    if (id != null) { try { setText(localStorage.getItem(`yuc.ai.draft.${id}`) || ""); } catch { setText(""); } }
  }, [active?.id]);
  useEffect(() => {
    const id = active?.id;
    if (id == null) return;
    try { if (text.trim()) localStorage.setItem(`yuc.ai.draft.${id}`, text); else localStorage.removeItem(`yuc.ai.draft.${id}`); } catch { /* ignore */ }
  }, [text, active?.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [active?.messages, stream, sending]);
  // таймер ожидания первого токена
  useEffect(() => {
    if (!sending) { setElapsed(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 250);
    return () => clearInterval(id);
  }, [sending]);

  async function openConv(id: number) {
    setError(null); setLoadingConv(true);
    try { const c = await api.aiConversation(id); setActive({ id: c.id, title: c.title, messages: c.messages }); }
    catch { /* ignore */ } finally { setLoadingConv(false); }
  }
  async function newChat() {
    setError(null);
    try { const c = await api.aiCreateConversation(); setActive({ id: c.id, title: c.title, messages: [] }); await loadConvs(); }
    catch { /* ignore */ }
  }
  async function del(id: number) {
    try { await api.aiDeleteConversation(id); if (active?.id === id) setActive(null); await loadConvs(); }
    catch { /* ignore */ }
  }

  async function sendTo(convId: number, t: string, att: { url: string; preview: string } | null) {
    if ((!t && !att) || sending) return;
    setText(""); setAttachment(null); setError(null); setSending(true); setThinkOpen(true);
    // Контекст = отдел/модуль из pathname (currentLocation) + конкретная вкладка из
    // синглтона active-view (её публикует departments-workspace) → ассистент понимает
    // «эту вкладку», а не только раздел.
    const view = getActiveView();
    const loc = view
      ? (currentLocation ? `${currentLocation}, вкладка «${view}»` : `Вкладка «${view}»`)
      : currentLocation;
    const optimistic: AiMessage = { id: -1, conversation_id: convId, role: "user", content: t, attachment_url: att?.url ?? null, created_at: new Date().toISOString() };
    setActive((a) => (a && a.id === convId ? { ...a, messages: [...a.messages, optimistic] } : a));
    try {
      if (att) {
        // со скрином — vision через non-stream (codex exec -i), ответ приходит разом
        const r = await api.aiSend(convId, t || "Что на этом скрине?", att.url, loc || null);
        if (r.error) setError(r.error);
      } else {
        const acc: StreamState = { reasoning: "", content: "", image_url: null };
        setStream({ ...acc });
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        await api.aiSendStream(convId, t, (e) => {
          if (e.type === "reasoning") { acc.reasoning += e.text; setStream({ ...acc }); }
          else if (e.type === "content") { acc.content += e.text; setStream({ ...acc }); }
          else if (e.type === "image") { acc.image_url = e.url; setStream({ ...acc }); }
          else if (e.type === "error") { setError(e.text); }
        }, ctrl.signal, loc || null);
      }
    } catch (err) {
      // отмену пользователем (кнопка «Стоп») не показываем как ошибку
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        setError(err instanceof Error ? err.message : "Ошибка запроса");
      }
    } finally {
      abortRef.current = null;
      setStream(null);
      setSending(false);
      // перечитываем беседу из БД (там сохранены user+assistant)
      try { const c = await api.aiConversation(convId); setActive({ id: c.id, title: c.title, messages: c.messages }); } catch { /* ignore */ }
      void loadConvs();
    }
  }

  function send(override?: string) {
    const t = (override ?? text).trim();
    if (!active) return;
    void sendTo(active.id, t, attachment);
  }

  // Создать новую беседу и сразу отправить в неё промпт (для «✨ Кратко» из чата).
  async function askNew(prompt: string) {
    try {
      const c = await api.aiCreateConversation();
      setActive({ id: c.id, title: c.title, messages: [] });
      void loadConvs();
      await sendTo(c.id, prompt, null);
    } catch { /* ignore */ }
  }

  function stop() { abortRef.current?.abort(); }

  async function saveTitle() {
    const nt = titleDraft.trim();
    setRenaming(false);
    if (!active || !nt || nt === active.title) return;
    try {
      await api.aiRenameConversation(active.id, nt);
      setActive((a) => (a ? { ...a, title: nt } : a));
      void loadConvs();
    } catch { /* ignore */ }
  }

  // image_url вида /content/imagegen/files/<id>.png лежит под /api и требует Bearer →
  // AuthImage срезает /api, поэтому передаём с префиксом "/api".
  const renderImage = (url?: string | null) =>
    url ? (
      <AuthImage src={`/api${url}`} alt="изображение"
        className="mt-2 max-w-full rounded-[var(--radius-lg)]" />
    ) : null;

  // ── окно беседы ──
  // ⚠️ ПЕРЕПИСКА — ЗНАЧЕНИЕ, А НЕ РАННИЙ ВОЗВРАТ: в развёрнутой панели она
  // становится правой колонкой рядом со списком, как у переписки с человеком
  // (Максим, 04.09.2026: «сам чат открывается не как у обычного человека,
  // слева пропадает список»). Высота h-full вместо прежних 60dvh — иначе поле
  // ввода зависало посреди панели, а под ним оставалась пустота.
  const перепискаAI = active ? (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2">
          <button type="button" onClick={() => { if (встроенный) { onBack?.(); return; } setActive(null); void loadConvs(); }} className="text-[var(--textMuted)] hover:text-[var(--text)]"><ArrowLeft size={16} /></button>
          <IconSparkles size={16} className="shrink-0" style={{ color: AI_COLOR }} />
          {renaming ? (
            <input autoFocus value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => void saveTitle()}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void saveTitle(); } if (e.key === "Escape") setRenaming(false); }}
              className="field-sm min-w-0 flex-1" />
          ) : (
            <button type="button" onClick={() => { setTitleDraft(active.title || ""); setRenaming(true); }}
              data-tooltip="Переименовать беседу" className="glass-flat group flex min-w-0 flex-1 items-center gap-1 text-left">
              <span className="truncate text-[12px] font-semibold text-[var(--text)]">{active.title || "Новый чат"}</span>
              <IconEdit size={12} className="shrink-0 text-[var(--textMuted)] opacity-0 transition group-hover:opacity-100" />
            </button>
          )}
          {onToggleWide && (
            <button type="button" onClick={onToggleWide} data-tooltip={wide ? "Свернуть панель" : "Развернуть панель"}
              className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-full text-[var(--textMuted)] transition hover:text-[var(--text)] sm:inline-flex" style={{ boxShadow: "none" }}>
              {wide ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          )}
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto p-3">
          {loadingConv && <p className="py-6 text-center text-[12px] text-[var(--textMuted)]">Загрузка…</p>}
          {!loadingConv && active.messages.length === 0 && !stream && (
            <div className="py-4">
              <EmptyState icon={<IconSparkles size={20} />} title="Спросите ассистента"
                hint="Он знает базу знаний портала, методички вкладок и ваши данные. Отвечает GPT." accent={AI_COLOR} />
              <div className="mx-auto mt-1 flex max-w-[320px] flex-wrap justify-center gap-1.5">
                {STARTER_CHIPS.map((c) => (
                  <button key={c} type="button" onClick={() => void send(c)} disabled={sending}
                    className="glass-flat rounded-[var(--radius-md)] border px-2.5 py-1 text-[11px] text-[var(--textMuted)] transition hover:text-[var(--text)] disabled:opacity-50"
                    style={{ borderColor: `color-mix(in srgb, ${AI_COLOR} 40%, transparent)`, boxShadow: "none" }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          {active.messages.map((m) => {
            const mine = m.role === "user";
            const nav = mine ? null : extractNav(m.content || "");
            const body = nav ? nav.clean : m.content;
            return (
              <div key={m.id === -1 ? "tmp" : m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`flex max-w-[88%] flex-col ${mine ? "items-end" : "items-start"}`}>
                  <div className={`rounded-[var(--radius-lg)] px-3 py-2 text-[12px] ${
                    mine ? "bg-[var(--bubble-mine)] text-[var(--text)]" : "bg-[var(--surfaceElevated)] text-[var(--text)]"
                  }`} style={mine ? undefined : { boxShadow: `0 0 0 1px color-mix(in srgb, ${AI_COLOR} 30%, transparent)` }}>
                    {body && (mine
                      ? <span className="whitespace-pre-wrap break-words">{body}</span>
                      : <ChatMarkdown text={body} />)}
                    {renderImage(m.attachment_url)}
                    {renderImage(m.image_url)}
                  </div>
                  {/* кнопки-переходы из ответа ассистента (маркер [[NAV:]]) */}
                  {nav && nav.navs.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1.5 pl-1">
                      {nav.navs.map((n) => (
                        <button key={n.path + n.label} type="button"
                          onClick={() => { router.push(n.path); onClose?.(); }}
                          className="glass-flat inline-flex items-center gap-1 rounded-[var(--radius-md)] border px-2.5 py-1 text-[11px] font-semibold transition"
                          style={{ borderColor: `color-mix(in srgb, ${AI_COLOR} 45%, transparent)`, color: `color-mix(in srgb, ${AI_COLOR} 72%, var(--text))`, boxShadow: "none" }}>
                          <ArrowUpRight size={12} /> {n.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Ответ ассистента можно оформить в работу — заявкой или задачей (с предпросмотром) */}
                  {!mine && body && <AiSpecActions text={body} />}
                </div>
              </div>
            );
          })}

          {/* живой стриминг ответа */}
          {stream && (
            <div className="flex justify-start">
              <div className="max-w-[88%] rounded-[var(--radius-lg)] bg-[var(--surfaceElevated)] px-3 py-2 text-[12px] text-[var(--text)]"
                style={{ boxShadow: `0 0 0 1px color-mix(in srgb, ${AI_COLOR} 30%, transparent)` }}>
                {/* рассуждение (сворачиваемое) */}
                {stream.reasoning && (
                  <div className="mb-1.5">
                    <button type="button" onClick={() => setThinkOpen((v) => !v)}
                      className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: AI_COLOR }}>
                      <IconChevronDown size={13} className={`transition-transform ${thinkOpen ? "" : "-rotate-90"}`} />
                      Рассуждение
                    </button>
                    {thinkOpen && (
                      <div className="mt-1 whitespace-pre-wrap break-words border-l-2 pl-2 text-[11px] text-[var(--textMuted)]"
                        style={{ borderColor: `color-mix(in srgb, ${AI_COLOR} 40%, transparent)` }}>
                        {stream.reasoning}
                      </div>
                    )}
                  </div>
                )}
                {stream.content
                  ? <ChatMarkdown text={extractNav(stream.content).clean} />
                  : !stream.reasoning && (
                    <span className="flex items-center gap-2 text-[var(--textMuted)]">
                      <IconSpinner size={14} className="animate-spin" style={{ color: AI_COLOR }} />
                      GPT думает… {elapsed}s
                    </span>
                  )}
                {renderImage(stream.image_url)}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-[var(--radius-lg)] border border-[var(--danger)] bg-[color-mix(in_srgb,var(--danger)_12%,transparent)] px-3 py-2 text-[11px] text-[var(--danger)]">
              {error}
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[var(--border)] p-2.5">
          {/* превью прикреплённого скрина */}
          {attachment && (
            <div className="mb-2 flex items-center gap-2">
              <div className="relative">
                <img src={attachment.preview} alt="вложение" className="h-14 w-14 rounded-[var(--radius-md)] object-cover" />
                <button type="button" onClick={() => setAttachment(null)} aria-label="Убрать"
                  className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--danger)] text-white">
                  <IconClose size={11} />
                </button>
              </div>
              <span className="text-[11px] text-[var(--textMuted)]">Скрин приложен</span>
            </div>
          )}
          <div className="flex items-end gap-1.5">
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { void handleFile(e.target.files?.[0]); if (fileRef.current) fileRef.current.value = ""; }} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || sending} aria-label="Прикрепить скрин"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--textMuted)] transition hover:text-[var(--text)] disabled:opacity-50">
              {uploading ? <IconSpinner size={16} className="animate-spin" /> : <IconAttach size={16} />}
            </button>
            {/* Пилюля здесь НАМЕРЕННО, в отличие от многострочных полей (там TEXTAREA_CLS):
                это строка чата в одну строку между двумя круглыми кнопками, Enter отправляет,
                а подрастает она редко и максимум до max-h-32. */}
            <textarea className={`${inputCls} max-h-32 resize-none`} rows={1} placeholder="Сообщение или скрин…" value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={(e) => { const img = Array.from(e.clipboardData.files).find((f) => f.type.startsWith("image/")); if (img) { e.preventDefault(); void handleFile(img); } }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} />
            {!sending && <VoiceButton onText={(t) => setText((v) => (v ? v.trimEnd() + " " : "") + t)} />}
            {sending ? (
              <button type="button" onClick={stop} data-tooltip="Остановить генерацию" aria-label="Остановить"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
                style={{ background: AI_COLOR }}>
                <Square size={12} fill="#fff" />
              </button>
            ) : (
              <button type="button" onClick={() => void send()} disabled={!text.trim() && !attachment}
                className="glass-btn-primary inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-50"
                style={{ background: AI_COLOR }}>
                <IconSend size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
  ) : null;

  // ── список бесед ──
  const q = query.trim().toLowerCase();
  const shown = q ? convs.filter((c) => c.title.toLowerCase().includes(q)) : convs;
  const списокAI = (
    <div className="p-2">
      <button type="button" onClick={() => void newChat()}
        className="glass-flat mb-2 flex w-full items-center justify-center gap-1.5 rounded-[var(--radius-lg)] border px-3 py-2 text-[12px] font-semibold transition-all"
        style={{ borderColor: `color-mix(in srgb, ${AI_COLOR} 55%, transparent)`, color: `color-mix(in srgb, ${AI_COLOR} 70%, var(--text))` }}>
        <IconPlus size={15} /> Новый чат
      </button>
      {shown.length === 0 && (q
        ? <EmptyState icon={<IconSearch size={20} />} title="Ничего не найдено" hint="Попробуйте другой запрос." />
        : <EmptyState icon={<IconSparkles size={20} />} title="Бесед пока нет" hint="Нажмите «Новый чат» — ассистент знает базу знаний портала и ваши данные." accent={AI_COLOR} />)}
      <ul className="flex flex-col">
        {shown.map((c) => (
          <li key={c.id} className="group flex items-center gap-1">
            <button type="button" onClick={() => void openConv(c.id)}
              className="glass-flat flex min-w-0 flex-1 items-center gap-2.5 rounded-[var(--radius-lg)] border border-transparent px-3 py-2 text-left transition-all hover:border-[color-mix(in_srgb,var(--text)_15%,transparent)]">
              <IconSparkles size={15} className="shrink-0" style={{ color: AI_COLOR }} />
              <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--text)]">{c.title || "Новый чат"}</span>
            </button>
            <button type="button" onClick={() => void del(c.id)} aria-label="Удалить"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[var(--textMuted)] opacity-0 transition hover:text-[var(--danger)] group-hover:opacity-100">
              <IconTrash size={14} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  // Встроенный вид: список слева рисует чат, здесь только переписка. Пока
  // беседа не загрузилась — держим пустую колонку, чтобы не мигал чужой список.
  if (встроенный) return перепискаAI ?? <div className="flex-1" />;

  // Развёрнутая панель: список слева, переписка справа — как в чате с человеком.
  // В свёрнутой (460 px) две колонки не поместятся, там прежнее переключение.
  if (wide) {
    return (
      <div className="flex h-full min-h-0">
        <aside className="w-[210px] shrink-0 overflow-y-auto border-r border-[var(--border)]">{списокAI}</aside>
        <section className="flex min-w-0 flex-1 flex-col">
          {перепискаAI ?? (
            <div className="flex h-full items-center justify-center px-6">
              <EmptyState icon={<IconSparkles size={20} />} title="Выберите беседу"
                          hint="Слева беседы с ассистентом. Откройте любую — переписка появится здесь."
                          accent={AI_COLOR} />
            </div>
          )}
        </section>
      </div>
    );
  }
  return перепискаAI ?? списокAI;
}


// ── Информация о канале: участники, общие файлы и ссылки ────────────────────
// ⚠️ В образце это третья колонка. В панели-кружке трёх колонок не поместится,
// поэтому она открывается на месте переписки по значку «i» — содержимое то же.
function ИнформацияОКанале({ info, onClose }: { info: TmChannelInfo | null; onClose: () => void }) {
  if (!info) return <div className="flex-1 p-4 text-[12px] text-[var(--textMuted)]">Загружаем…</div>;
  return (
    <div className="flex-1 space-y-3 overflow-y-auto p-3">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-semibold text-[var(--text)]">Информация о канале</span>
        <button type="button" onClick={onClose} className="glass-flat ml-auto text-[var(--textMuted)]"><IconClose size={14} /></button>
      </div>
      {info.description && <p className="text-[11px] text-[var(--textMuted)]">{info.description}</p>}

      <div>
        <div className="field-label pb-1">Участники · {info.members.length}</div>
        <ul className="flex flex-col">
          {info.members.map((m) => (
            <li key={m.id} className="data-row flex items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-[12px] text-[var(--text)]">
              <UserAvatar name={m.name} color="var(--primary)" size={20} />
              <span className="min-w-0 flex-1 truncate">{m.name}</span>
              {m.role === "admin" && <span className="shrink-0 text-[11px] text-[var(--textMuted)]">админ</span>}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="field-label pb-1">Общие файлы · {info.files.length}</div>
        {!info.files.length && <p className="text-[11px] text-[var(--textMuted)]">Файлов ещё не присылали.</p>}
        <ul className="flex flex-col">
          {info.files.map((f) => (
            <li key={f.id}>
              <a href={`${API_BASE}${f.url}`} target="_blank" rel="noreferrer"
                 className="data-row flex items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-[12px] text-[var(--text)]">
                <FileText size={13} className="shrink-0 text-[var(--textMuted)]" />
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                <span className="shrink-0 text-[11px] text-[var(--textMuted)]">
                  {f.size >= 1048576 ? `${(f.size / 1048576).toFixed(1)} МБ` : `${Math.max(1, Math.round(f.size / 1024))} КБ`}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="field-label pb-1">Ссылки · {info.links.length}</div>
        {!info.links.length && <p className="text-[11px] text-[var(--textMuted)]">Ссылок в переписке пока нет.</p>}
        <ul className="flex flex-col">
          {info.links.map((l, i) => (
            <li key={i}>
              <a href={l.url} target="_blank" rel="noreferrer"
                 className="data-row block truncate rounded-[var(--radius-md)] px-1.5 py-1 text-[12px] text-[var(--primary)]">
                {l.host}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


// ── Создание канала ─────────────────────────────────────────────────────────
function СозданиеКанала({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [имя, setИмя] = useState("");
  const [описание, setОписание] = useState("");
  const [закрытый, setЗакрытый] = useState(false);
  const [шлём, setШлём] = useState(false);
  const [ошибка, setОшибка] = useState("");

  async function создать() {
    const n = имя.trim();
    if (!n) return;
    setШлём(true); setОшибка("");
    try { await api.tmCreateChannel({ name: n, description: описание.trim(), is_private: закрытый }); onCreated(); }
    catch (e) { setОшибка(e instanceof Error ? e.message : "Не удалось создать канал"); }
    finally { setШлём(false); }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 p-4" style={{ zIndex: Z.MODAL }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="flex w-full max-w-[360px] flex-col gap-3 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-4 elev-modal">
        <span className="text-[12px] font-semibold text-[var(--text)]">Новый канал</span>
        <label className="flex flex-col gap-1">
          <span className="field-label">Имя</span>
          <input value={имя} onChange={(e) => setИмя(e.target.value)} placeholder="например, маркетинг"
                 className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[12px] text-[var(--text)] focus:border-[var(--primary)] focus:outline-none" />
          <span className="text-[11px] text-[var(--textMuted)]">Пробелы заменятся дефисами: «ит поддержка» → «ит-поддержка».</span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="field-label">О чём канал</span>
          <input value={описание} onChange={(e) => setОписание(e.target.value)} placeholder="Необязательно"
                 className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[12px] text-[var(--text)] focus:border-[var(--primary)] focus:outline-none" />
        </label>
        <label className="flex cursor-pointer items-start gap-2">
          <input type="checkbox" checked={закрытый} onChange={(e) => setЗакрытый(e.target.checked)} className="mt-0.5" />
          <span>
            <span className="block text-[12px] text-[var(--text)]">Закрытый канал</span>
            <span className="block text-[11px] text-[var(--textMuted)]">
              Его увидят и прочитают только участники. Открытый видит каждый в портале.
            </span>
          </span>
        </label>
        {ошибка && <span className="text-[11px] text-[var(--dangerInk)]">{ошибка}</span>}
        <div className="flex items-center gap-2">
          <Button variant="primary" className="ml-auto" disabled={шлём || !имя.trim()} onClick={() => void создать()}>Создать</Button>
          <Button onClick={onClose}>Отмена</Button>
        </div>
      </div>
    </div>
  );
}


/** Разделитель дня в ленте: «Сегодня», «Вчера», «15 июня». */
function датаЛенты(iso: string): string {
  const d = new Date(iso + "Z");
  if (Number.isNaN(d.getTime())) return "";
  const сейчас = new Date();
  if (d.toDateString() === сейчас.toDateString()) return "Сегодня";
  const вчера = new Date(сейчас); вчера.setDate(вчера.getDate() - 1);
  if (d.toDateString() === вчера.toDateString()) return "Вчера";
  return d.toLocaleDateString("ru-RU", {
    day: "numeric", month: "long",
    ...(d.getFullYear() === сейчас.getFullYear() ? {} : { year: "numeric" }),
  });
}
